import React, { useState, useEffect } from 'react';
import './FeedbackForm.scss';
import { useTranslation } from 'react-i18next';
import { useBridge } from '../../bridge/bridge-provider.tsx';
import { BridgeData } from '@/moment/bridge/client-bridge-defs.ts';

const FeedbackForm: React.FC = () => {
  const [feedback, setFeedback] = useState('');
  const [email, setEmail] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');
  const { t, i18n } = useTranslation();
  const bridge = useBridge();
  const [deviceInfo, setDeviceInfo] = useState<object | string | null>(null);

  useEffect(() => {
    const deviceInfoConst = bridge.getData(BridgeData.DEVICE_INFO);
    if (deviceInfoConst) {
      setDeviceInfo(deviceInfoConst);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmissionStatus('submitting');

    let mpId: string = 'null';

    if (window && window.location) {
      const params = new URLSearchParams(window.location.search);
      if (params.has('mpid')) {
        mpId = params.get('mpid')!;
      } else if (params.has('uid')) {
        mpId = params.get('uid')!;
      }
    }

    const payload = { feedback, email, mpId };

    try {
      const response = await fetch('/api/feedback', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      setSubmissionStatus('success');
      setFeedback('');
      setEmail('');
    } catch (error) {
      setSubmissionStatus('error');
    }
  };

  return (
    <main className="legal-feedback">
      <form onSubmit={handleSubmit} data-gtm-id="feedback-form">
        <h4>{t('feedback.header', 'Please provide us with the following:')}</h4>
        {deviceInfo && <p>MPID: {(deviceInfo as any).unique_id}</p>}
        <label htmlFor="feedback-input">
          <p>{t('feedback.feedback.label', 'Your Feedback')}</p>
          <textarea
            id="feedback-input"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            required
            placeholder={t(
              'feedback.feedback.placeholder',
              'Add your feedback here...'
            )}
            data-gtm-id="feedback-input"
          />
        </label>
        <label htmlFor="email-input">
          <p>
            {t(
              'feedback.email.label',
              'Your email address so that we can contact you'
            )}
          </p>
          <input
            id="email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,63}$"
            placeholder={t('feedback.email.placeholder', 'Email address')}
            maxLength={256}
            data-gtm-id="email-input"
          />
        </label>
        <button type="submit" disabled={submissionStatus === 'submitting'}>
          {submissionStatus === 'submitting'
            ? t('feedback.submitting', 'Submitting...')
            : t('feedback.givefeedback', 'Give Feedback')}
        </button>
      </form>
      {submissionStatus === 'success' && (
        <p className="success-message">
          {t('feedback.success', 'Thank you for your feedback!')}
        </p>
      )}
      {submissionStatus === 'error' && (
        <p className="error-message">
          {t('feedback.error', 'An error occurred. Please try again.')}
        </p>
      )}
    </main>
  );
};

export default FeedbackForm;
